import { useState, type ReactElement } from 'react';
import { Link, useNavigate, useOutletContext, useParams } from 'react-router';
import { creerAutomatisation } from '../automatisations.js';
import { EchecApi } from '../api.js';
import { CHEMINS } from '../routes.js';

interface Espace { readonly code: string; readonly nom: string; readonly role: string }

/**
 * CREER UNE AUTOMATISATION [29-E].
 *
 * CREER, PUIS OUVRIR. On ne revient pas a la liste : l intention de l
 * utilisateur n est pas d ajouter une ligne, c est de CONSTRUIRE une
 * automatisation. L objet et son brouillon naissent dans la meme transaction
 * cote moteur, donc le 201 suffit a entrer dans l editeur.
 *
 * C est la reserve de 29-C levee, pas contournee : « sans l editeur on
 * creerait une coquille vide ». L editeur existe desormais, et le brouillon
 * avec l objet -- la condition qui justifiait le refus a disparu.
 *
 * AUCUN CODE, AUCUNE DERIVATION. L identite publique est un UUID [28-BZ ne s
 * applique pas ici] : rien a deriver depuis le nom, l URL n est pas parlante.
 *
 * LA NAVIGATION NE COMMENCE QU APRES UN 201 CERTAIN. En echec on reste dans le
 * formulaire, saisie conservee, message affiche -- jamais de navigation
 * partielle.
 */
export function NouvelleAutomatisation(): ReactElement {
  const espace = useOutletContext<Espace>();
  // Le projet vient du segment d URL [contrat 28-BJ].
  const { projet } = useParams<{ projet: string }>();
  const navigate = useNavigate();
  const [nom, setNom] = useState('');
  const [description, setDescription] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | undefined>(undefined);
  const pret = nom.trim() !== '' && !envoi;

  const surCreer = async (): Promise<void> => {
    setEnvoi(true); setErreur(undefined);
    try {
      const a = await creerAutomatisation(espace.code, projet ?? '', {
        nom: nom.trim(),
        description: description.trim() === '' ? undefined : description.trim() });
      /* 29-F, dette 61 : REMPLACER et non EMPILER. 28-CN fait de la creation
       * la condition d entree dans l editeur, pas une etape a revisiter :
       * Precedent depuis l editeur doit ramener a la LISTE, jamais a ce
       * formulaire vide. Le retour explicite de l editeur dit la meme
       * destination sans dependre de l historique. */
      navigate(CHEMINS.editeur(espace.code, projet ?? '', a.id),
        { replace: true });
    } catch (e) {
      const c = e instanceof EchecApi ? e.detail.code : '';
      setErreur(c === 'droit_insuffisant'
        ? 'Votre role ne permet pas de creer une automatisation ici.'
        : c === 'corps_invalide'
          ? 'Le nom est obligatoire et ne peut pas depasser 200 caracteres.'
          : e instanceof Error ? e.message : 'Echec inattendu.');
    } finally { setEnvoi(false); }
  };

  return (
    <section>
      <p><Link to={CHEMINS.automatisations(espace.code, projet ?? '')}>
        &larr; Automatisations</Link></p>
      <h1>Nouvelle automatisation</h1>
      <label>Nom
        <input value={nom} onChange={(e) => setNom(e.target.value)}
          placeholder="Reservation" /></label>
      <p className="discret">Le nom lisible, tel qu il s affichera partout.
        Accents, espaces et majuscules sont permis.</p>
      <label>Description
        <input value={description}
          onChange={(e) => setDescription(e.target.value)} /></label>
      <p className="discret">Facultative. Une phrase pour situer cette
        automatisation.</p>
      {erreur !== undefined && <p className="message message--refus">{erreur}</p>}
      <p><button className="bouton" disabled={!pret}
        onClick={() => { void surCreer(); }}>Creer et ouvrir</button></p>
    </section>
  );
}
